import { Box, Button } from "@analyticsMD/quil";
import { grey } from "@mui/material/colors";
import moment from "moment/moment";
import type { FC } from "react";
import EDD from "../types/EDD";
import PatientDisposition from "../types/PatientDisposition";

type ContextQuote = NonNullable<
  NonNullable<PatientDisposition["suggestion_context"]>["quotes"]
>[number];

/** `probability` is a 0–1 model score; missing or non-finite values show as N/A (not 0%). */
function formatModelPredictionProbability(
  probability: number | null | undefined,
): string {
  if (probability == null || !Number.isFinite(probability)) {
    return "N/A";
  }
  return `${(probability * 100).toFixed(1)}%`;
}

const sectionLabelSx = {
  fontWeight: 700,
  fontSize: "0.875rem",
  color: grey[900],
  marginBottom: "0.5rem",
};

const sectionValueSx = {
  fontWeight: 400,
  fontSize: "0.875rem",
  color: grey[900],
  lineHeight: 1.5,
};

const listSx = {
  margin: 0,
  paddingLeft: "1.125rem",
  listStyleType: "disc",
  "& li": {
    fontSize: "0.875rem",
    color: grey[900],
    lineHeight: 1.5,
    marginBottom: "0.375rem",
    "&:last-child": { marginBottom: 0 },
  },
};

const footerSx = {
  fontStyle: "italic",
  fontSize: "0.75rem",
  color: grey[600],
  marginTop: "1.25rem",
  lineHeight: 1.45,
};

const contentPaddingSx = {
  padding: "1.25rem 1.5rem",
  maxHeight: "70vh",
  overflowY: "auto",
};

export interface SuggestionExplainabilityPopoverContentProps {
  suggestion: PatientDisposition | EDD;
  suggestionType: string;
  onClose: () => void;
}

const formatTherapyQuote = (quote: ContextQuote) => {
  const dateFormatted = quote.time ? moment(quote.time).format("MM/DD") : "";
  return (
    <Box component="span" sx={sectionValueSx}>
      {`"${quote.quote}" - `}
      <i>{`(${quote.author}, ${dateFormatted})`}</i>
    </Box>
  );
};

const DispositionExplainabilityContent: FC<{
  suggestion: PatientDisposition;
  onClose: () => void;
}> = ({ suggestion, onClose }) => {
  const features = suggestion.suggestion_context?.quotes?.filter(
    (q) => q.type === "feature",
  );
  const hasFeatures = features && features.length > 0;

  const notes = suggestion.suggestion_context?.quotes?.filter(
    (q) => q.type !== "feature",
  );
  const hasNotes = notes && notes.length > 0;

  const probability = suggestion.suggestion_context?.probability;
  const percentage = formatModelPredictionProbability(probability);

  return (
    <Box sx={contentPaddingSx}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "1rem",
        }}
      >
        <Button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          size="small"
          sx={{
            textTransform: "none",
            minWidth: "auto",
            padding: 0,
            fontSize: "0.875rem",
            fontWeight: 400,
          }}
          color="primary"
        >
          Hide
        </Button>
      </Box>

      <Box sx={{ marginBottom: "1.25rem" }}>
        <Box component="div" sx={sectionLabelSx}>
          Suggested disposition
        </Box>
        <Box component="div" sx={sectionValueSx}>
          {suggestion.disposition?.display_name}
        </Box>
      </Box>

      <Box sx={{ marginBottom: "1.25rem" }}>
        <Box component="div" sx={sectionLabelSx}>
          Model Prediction Probability
        </Box>
        <Box component="div" sx={sectionValueSx}>
          {percentage}
        </Box>
      </Box>

      {hasFeatures ? (
        <Box sx={{ marginBottom: hasNotes ? "1.25rem" : 0 }}>
          <Box component="div" sx={sectionLabelSx}>
            Key features & insights
          </Box>
          <Box component="ul" sx={listSx}>
            {features.map((quote, index) => (
              <Box component="li" key={index}>
                {quote.quote}
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}

      {hasNotes ? (
        <Box sx={{ marginBottom: 0 }}>
          <Box component="div" sx={sectionLabelSx}>
            Relevant Therapy Notes
          </Box>
          <Box component="ul" sx={listSx}>
            {notes.map((quote, index) => (
              <Box component="li" key={index}>
                {formatTherapyQuote(quote)}
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}

      {hasFeatures ? (
        <Box component="div" sx={footerSx}>
          *Key features and insights from 250+ features
        </Box>
      ) : null}
    </Box>
  );
};

const EddExplainabilityContent: FC<{
  suggestion: EDD;
  onClose: () => void;
}> = ({ suggestion, onClose }) => {
  const percentage = formatModelPredictionProbability(
    suggestion.suggestion_context?.probability,
  );

  return (
    <Box sx={contentPaddingSx}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "1rem",
        }}
      >
        <Button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          size="small"
          sx={{
            textTransform: "none",
            minWidth: "auto",
            padding: 0,
            fontSize: "0.875rem",
            fontWeight: 400,
          }}
          color="primary"
        >
          Hide
        </Button>
      </Box>

      <Box sx={{ marginBottom: "1.25rem" }}>
        <Box component="div" sx={sectionLabelSx}>
          Model Prediction Probability
        </Box>
        <Box component="div" sx={sectionValueSx}>
          {percentage}
        </Box>
      </Box>

      <Box sx={{ marginBottom: 0 }}>
        <Box component="div" sx={sectionLabelSx}>
          Key features & insights
        </Box>
        <Box component="ul" sx={listSx}>
          {suggestion.suggestion_context?.quotes?.map((quote, index) => (
            <Box component="li" key={index}>
              {quote.quote}
            </Box>
          ))}
        </Box>
      </Box>

      <Box component="div" sx={footerSx}>
        *Key features and insights from 500+ features
      </Box>
    </Box>
  );
};

const SuggestionExplainabilityPopoverContent: FC<
  SuggestionExplainabilityPopoverContentProps
> = ({ suggestion, suggestionType, onClose }) =>
  suggestionType === "disposition" ? (
    <DispositionExplainabilityContent
      suggestion={suggestion as PatientDisposition}
      onClose={onClose}
    />
  ) : (
    <EddExplainabilityContent suggestion={suggestion as EDD} onClose={onClose} />
  );

export default SuggestionExplainabilityPopoverContent;
